/**
 * Thin wrapper around window.Telegram.WebApp, so the app also runs in a
 * plain browser during development.
 */

export function getWebApp(): TelegramWebApp | null {
    return typeof window !== 'undefined' && window.Telegram?.WebApp ? window.Telegram.WebApp : null;
}

export function initTelegram(): void {
    const tg = getWebApp();
    if (!tg) return;
    try {
        tg.ready();
        tg.expand();
        (tg as unknown as { setHeaderColor?: (c: string) => void }).setHeaderColor?.('#060a06');
        (tg as unknown as { setBackgroundColor?: (c: string) => void }).setBackgroundColor?.('#060a06');
    } catch { /* ignore */ }
}

export function getInitData(): string {
    return getWebApp()?.initData || '';
}

export function getTgUser(): TelegramWebAppUser | null {
    return getWebApp()?.initDataUnsafe?.user || null;
}

export function getStartParam(): string | null {
    return getWebApp()?.initDataUnsafe?.start_param || null;
}

export function haptic(kind: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning' | 'selection'): void {
    const tg = getWebApp();
    if (!tg) return;
    try {
        if (kind === 'success' || kind === 'error' || kind === 'warning') tg.HapticFeedback.notificationOccurred(kind);
        else if (kind === 'selection') tg.HapticFeedback.selectionChanged();
        else tg.HapticFeedback.impactOccurred(kind);
    } catch { /* ignore */ }
}

export function showAlert(message: string): Promise<void> {
    const tg = getWebApp();
    if (tg && tg.initData) return new Promise(resolve => tg.showAlert(message, () => resolve()));
    window.alert(message);
    return Promise.resolve();
}

export function showConfirm(message: string): Promise<boolean> {
    const tg = getWebApp();
    if (tg && tg.initData) return new Promise(resolve => tg.showConfirm(message, ok => resolve(!!ok)));
    return Promise.resolve(window.confirm(message));
}

/** Telegram links (channels, support, share) stay inside Telegram. */
export function openTelegramLink(url: string): void {
    const tg = getWebApp() as unknown as { openTelegramLink?: (u: string) => void } | null;
    try {
        if (tg && typeof tg.openTelegramLink === 'function' && /^https:\/\/t\.me\//.test(url)) { tg.openTelegramLink(url); return; }
    } catch { /* fall through */ }
    window.open(url, '_blank');
}

/** Payment pages and the website open in the browser; the app stays open behind them. */
export function openExternal(url: string): void {
    if (/^https:\/\/t\.me\//.test(url)) return openTelegramLink(url);
    const tg = getWebApp();
    try {
        if (tg && tg.initData && typeof tg.openLink === 'function') { tg.openLink(url); return; }
    } catch { /* fall through */ }
    window.open(url, '_blank', 'noopener');
}

export async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const el = document.createElement('textarea');
            el.value = text; el.setAttribute('readonly', ''); el.style.position = 'fixed'; el.style.opacity = '0';
            document.body.appendChild(el); el.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(el);
            return ok;
        } catch { return false; }
    }
}
